import {StarRating as StarRatingType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_reviews.types';
import {useTranslation} from '@aboutyou/i18n';
import {StarRating, Text} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';

type SectionAlignment = 'center' | 'left';

export interface OverallRatingProps {
  overallRating?: number;
  overallStarRating: StarRatingType;
  reviewCount: number;
  reduceBottomMargin?: boolean;
  disableTitle?: boolean;
  alignment?: SectionAlignment;
  className?: string;
  onClick?: VoidFunction;
}

export const OverallRating = ({
  overallStarRating,
  overallRating,
  reviewCount,
  reduceBottomMargin,
  disableTitle,
  alignment = 'center',
  className,
  onClick,
  ref,
}: OverallRatingProps & {ref?: React.Ref<HTMLDivElement>}) => {
  const {t} = useTranslation();

  const starRatingNormalized = starRatingMap(overallStarRating);

  const quantityReviewLabel =
    reviewCount === 1
      ? t('{ratingsQuantity} rating', {ratingsQuantity: reviewCount})
      : t('{ratingsQuantity} ratings', {ratingsQuantity: reviewCount});

  return (
    <OverallRatingWrapper
      data-testid="overallRatingSection"
      reduceBottomMargin={reduceBottomMargin}
      alignment={alignment}
      className={className}
      ref={ref}
      onClick={onClick}
    >
      {!disableTitle && (
        <Title size="px22" weight="bold" data-testid="ratingsTitle">
          {t('Customer reviews')}
        </Title>
      )}
      <OverallStarsContainer>
        <StarRating
          value={starRatingNormalized}
          size="L"
          dataTestid={`overallStarsRating-${starRatingNormalized}`}
          outlineColor={starRatingNormalized > 0 ? 'Black' : 'Grey1'}
        />
        {overallRating && (
          <Text data-testid="overallRating">{overallRating}</Text>
        )}
      </OverallStarsContainer>
      <Text color="Grey1" data-testid="quantityRatingsLabel">
        {quantityReviewLabel}
      </Text>
    </OverallRatingWrapper>
  );
};

/**
 * Will fill the stars starting with 1 and adding 0.5.
 * 0: 0,
 * 1: 1,
 * 2: 1.5,
 * .
 * .
 * 8: 4.5,
 * 9: 5,
 * @param rating
 * @returns
 */
//
export const starRatingMap = (rating: StarRatingType) => {
  if (rating === 0) return rating;
  return rating % 2 === 0 ? rating / 2 + 0.5 : Math.round(rating / 2);
};

const Title = styled(Text)`
  margin-bottom: 1rem;
`;

const OverallRatingWrapper = styled.div<{
  reduceBottomMargin?: boolean;
  alignment: SectionAlignment;
}>`
  align-items: ${({alignment}) =>
    alignment === 'center' ? 'center' : 'start'};
  display: flex;
  flex-direction: column;

  ${({reduceBottomMargin}) =>
    !reduceBottomMargin &&
    css`
      margin-bottom: 1rem;
    `}
`;

export const OverallStarsContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  text-align: center;
`;
